import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Users, ArrowLeft, Loader2, ChevronRight, Star,
  Headphones, Mic, BookOpen, PenTool, Calendar, Target, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_CONFIG = {
  listening: { icon: Headphones, color: '#2563eb', label: 'Listening' },
  speaking: { icon: Mic, color: '#9333ea', label: 'Speaking' },
  reading: { icon: BookOpen, color: '#16a34a', label: 'Reading' },
  writing: { icon: PenTool, color: '#ea580c', label: 'Writing' },
};

const WIDA_COLORS = {
  1: '#dc2626', 2: '#ea580c', 3: '#ca8a04', 4: '#2563eb', 5: '#16a34a', 6: '#059669',
};

const ParentDashboard = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childResults, setChildResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const res = await axios.get(`${API}/parent/children`);
        setChildren(res.data.children || []);
      } catch (e) {
        if (e.response?.status === 403) {
          toast.error('Parent access required.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchChildren();
  }, []);

  const loadChildResults = async (studentId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/parent/child/${studentId}/results`);
      setChildResults(res.data);
    } catch (e) {
      toast.error('Failed to load child results.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectChild = (child) => {
    setSelectedChild(child);
    loadChildResults(child.student_id);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="parent-dashboard">
      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="parent-title">
        Parent Portal
      </h1>
      <p className="text-sm text-gray-500 mb-6">
        View your child's language-proficiency progress and teacher feedback
      </p>

      {/* No Children Linked */}
      {children.length === 0 && !selectedChild && (
        <div className="text-center py-16 border-2 border-dashed border-gray-200" data-testid="no-children">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <h3 className="font-semibold text-gray-500 mb-1">No Children Linked</h3>
          <p className="text-sm text-gray-400">
            Ask your child's teacher to link your account to view their assessment results.
          </p>
        </div>
      )}

      {/* Child Selection */}
      {children.length > 0 && !selectedChild && (
        <div className="space-y-3" data-testid="children-list">
          {children.map(child => (
            <div
              key={child.student_id}
              className="border-2 border-gray-200 p-5 cursor-pointer hover:border-[#002FA7] transition-colors flex items-center justify-between"
              onClick={() => handleSelectChild(child)}
              data-testid={`child-card-${child.student_id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center font-bold text-lg">
                  {child.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <div className="font-semibold">{child.name}</div>
                  <div className="text-sm text-gray-500">
                    {child.grade_level && `Grade ${child.grade_level}`}
                    {child.last_assessed && ` • Last assessed ${new Date(child.last_assessed).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {child.latest_wida_level && (
                  <div className="text-center">
                    <div className="text-lg font-bold" style={{ color: WIDA_COLORS[child.latest_wida_level] }}>
                      L{child.latest_wida_level}
                    </div>
                    <div className="text-xs text-gray-500">{child.latest_wida_name}</div>
                  </div>
                )}
                <ChevronRight size={20} className="text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Child Detail View */}
      {selectedChild && (
        <div data-testid="child-detail">
          <Button variant="ghost" size="sm" className="mb-4" onClick={() => { setSelectedChild(null); setChildResults(null); }}>
            <ArrowLeft size={16} className="mr-2" /> Back to children
          </Button>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-[#002FA7] text-white flex items-center justify-center font-bold text-xl">
              {selectedChild.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-xl font-bold">{selectedChild.name}</h2>
              <p className="text-sm text-gray-500">
                {selectedChild.grade_level && `Grade ${selectedChild.grade_level}`}
                {selectedChild.latest_score != null && ` • Overall: ${selectedChild.latest_score}`}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              data-testid="export-child-data-btn"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/compliance/parent-export/${selectedChild.student_id}`, {
                    responseType: 'blob',
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `mosaic_student_data_${selectedChild.name?.replace(/\s/g, '_')}.json`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Data exported successfully');
                } catch (e) {
                  toast.error('Failed to export data');
                }
              }}
            >
              <Target size={14} className="mr-1" /> Export My Child's Data
            </Button>
          </div>

          {detailLoading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" /></div>
          ) : childResults ? (
            <>
              {/* Results */}
              {childResults.results?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Assessment History</h3>
                  <div className="space-y-3">
                    {childResults.results.map((r, idx) => (
                      <div key={idx} className="border-2 border-gray-200 p-4" data-testid={`parent-result-${idx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Calendar size={16} className="text-gray-400" />
                            <span className="text-sm">{r.scored_at ? new Date(r.scored_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown'}</span>
                            <span className="text-xs px-2 py-0.5 bg-gray-100">Grade {r.grade_level}</span>
                          </div>
                          <div className="font-bold text-lg" style={{ color: WIDA_COLORS[r.wida_level] || '#666' }}>
                            L{r.wida_level} — {r.wida_level_name}
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          {['listening', 'speaking', 'reading', 'writing'].map(d => {
                            const config = DOMAIN_CONFIG[d];
                            const DIcon = config.icon;
                            const score = r[`${d}_wida`] || 0;
                            return (
                              <div key={d} className="text-center border border-gray-100 p-2">
                                <DIcon size={14} className="mx-auto mb-1" style={{ color: config.color }} />
                                <div className="text-xs text-gray-500 capitalize">{d}</div>
                                <div className="font-bold" style={{ color: config.color }}>{score.toFixed(1)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Teacher Feedback */}
              {childResults.feedback?.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Teacher Feedback</h3>
                  <div className="space-y-3">
                    {childResults.feedback.map((f, idx) => (
                      <div key={idx} className="border border-gray-200 p-4" data-testid={`parent-feedback-${idx}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquare size={14} className="text-blue-500" />
                          <span className="text-sm font-semibold capitalize">{f.modality}</span>
                          <span className="text-sm font-bold ml-auto">{f.score}/{f.max_score}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{f.question_text}</p>
                        {f.teacher_feedback && (
                          <div className="bg-green-50 border border-green-200 p-3 mt-2">
                            <div className="text-xs font-semibold text-green-700 mb-1">Teacher Comment</div>
                            <p className="text-sm text-gray-700">{f.teacher_feedback}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {childResults.results?.length === 0 && childResults.feedback?.length === 0 && (
                <div className="text-center py-12 border-2 border-dashed border-gray-200">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">No assessment data available yet for {selectedChild.name}.</p>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ParentDashboard;
